import React, { useRef, useState } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { Upload, X, Plus } from "lucide-react";

export const BACKEND_ORIGIN = API.replace(/\/api\/?$/, "");

export const CATEGORY_OPTIONS = [
  { value: "cortes", label: "Cortes Premium" },
  { value: "charcuteria", label: "Charcutería" },
  { value: "quesos", label: "Quesos" },
  { value: "complementos", label: "Complementos" },
  { value: "condimentos", label: "Condimentos" },
  { value: "extras", label: "Extras" },
];

export function slugPreview(text) {
  return (text || "")
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase();
}

export function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <div className="text-[10px] tracking-[0.25em] uppercase text-white/60 mb-1">{label}</div>
      {children}
    </label>
  );
}

export function TextInput(props) {
  return (
    <input
      {...props}
      className={`w-full bg-arre-black border border-arre-border px-3 py-2 text-white text-sm focus:outline-none focus:border-arre-red ${props.className || ""}`}
    />
  );
}

export function TextArea(props) {
  return (
    <textarea
      {...props}
      className={`w-full bg-arre-black border border-arre-border px-3 py-2 text-white text-sm focus:outline-none focus:border-arre-red ${props.className || ""}`}
    />
  );
}

/** Sube un archivo al backend (/admin/upload) y regresa la URL completa. */
export async function uploadImageFile(file) {
  const formData = new FormData();
  formData.append("file", file);
  const token = localStorage.getItem("arre_token");
  const { data } = await axios.post(`${API}/admin/upload`, formData, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  return `${BACKEND_ORIGIN}${data.path}`;
}

/** Imagen única (portada): URL manual o subida de archivo, con preview. */
export function SingleImageUploader({ label, value, onChange }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setErr("");
    try {
      const url = await uploadImageFile(file);
      onChange(url);
    } catch (er) {
      setErr(er.response?.data?.detail || "Error al subir la imagen");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <Field label={label}>
      <div className="flex items-center gap-3">
        <div className="w-20 h-20 bg-arre-black border border-arre-border overflow-hidden flex-shrink-0">
          {value ? <img src={value} alt="" className="w-full h-full object-cover" /> : null}
        </div>
        <div className="flex-1 space-y-2">
          <TextInput
            type="text"
            placeholder="Pega una URL, o sube un archivo →"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
          />
          <div className="flex items-center gap-2">
            <input ref={inputRef} type="file" accept="image/*" onChange={handleFile} className="hidden" />
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={busy}
              className="text-[10px] tracking-widest uppercase border border-arre-border px-3 py-1.5 text-white/70 hover:text-white hover:border-white inline-flex items-center gap-1.5 disabled:opacity-50"
            >
              <Upload className="w-3.5 h-3.5" /> {busy ? "Subiendo..." : "Subir imagen"}
            </button>
            {value && (
              <button type="button" onClick={() => onChange("")} className="text-white/40 hover:text-arre-red">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          {err && <div className="text-arre-red text-xs">{err}</div>}
        </div>
      </div>
    </Field>
  );
}

/** Galería / carrusel: varias imágenes, subida múltiple, reordenables con flechas, eliminar. */
export function GalleryUploader({ label, values, onChange }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const list = values || [];

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    setErr("");
    try {
      const urls = [];
      for (const f of files) {
        // secuencial para no saturar el backend local
        // eslint-disable-next-line no-await-in-loop
        urls.push(await uploadImageFile(f));
      }
      onChange([...list, ...urls]);
    } catch (er) {
      setErr(er.response?.data?.detail || "Error al subir imágenes");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = (idx) => onChange(list.filter((_, i) => i !== idx));
  const move = (idx, dir) => {
    const next = [...list];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    onChange(next);
  };

  return (
    <Field label={label}>
      <div className="flex flex-wrap gap-2 mb-2">
        {list.map((url, i) => (
          <div key={`${url}-${i}`} className="relative w-16 h-16 border border-arre-border group">
            <img src={url} alt="" className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => remove(i)}
              className="absolute -top-1.5 -right-1.5 bg-arre-black border border-arre-border rounded-full w-5 h-5 flex items-center justify-center text-white/70 hover:text-arre-red"
              title="Quitar"
            >
              <X className="w-3 h-3" />
            </button>
            {i > 0 && (
              <button
                type="button"
                onClick={() => move(i, -1)}
                className="absolute bottom-0 left-0 bg-arre-black/80 text-white/70 hover:text-white text-[10px] px-1"
                title="Mover a la izquierda"
              >
                ←
              </button>
            )}
            {i < list.length - 1 && (
              <button
                type="button"
                onClick={() => move(i, 1)}
                className="absolute bottom-0 right-0 bg-arre-black/80 text-white/70 hover:text-white text-[10px] px-1"
                title="Mover a la derecha"
              >
                →
              </button>
            )}
          </div>
        ))}
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          className="w-16 h-16 border border-dashed border-arre-border flex items-center justify-center text-white/40 hover:text-white hover:border-white disabled:opacity-50"
          title="Agregar imágenes"
        >
          <Plus className="w-5 h-5" />
        </button>
        <input ref={inputRef} type="file" accept="image/*" multiple onChange={handleFiles} className="hidden" />
      </div>
      {busy && <div className="text-white/50 text-xs">Subiendo...</div>}
      {err && <div className="text-arre-red text-xs">{err}</div>}
    </Field>
  );
}

export function Modal({ title, onClose, children, wide = false }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center overflow-y-auto py-10 px-4">
      <div className={`bg-arre-surface border border-arre-border w-full ${wide ? "max-w-3xl" : "max-w-xl"} p-6 md:p-8 my-auto`}>
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display text-2xl tracking-wider uppercase text-white">{title}</h2>
          <button onClick={onClose} className="text-white/50 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
